import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Drives the ScapeSource ⟷ QuickBooks connection animation for both the
 * contractor and vendor panels.
 *
 * The OAuth handshake is a full-page redirect to Intuit and back, so the
 * "connecting" state has two halves:
 *   1. outbound — the user clicked Connect; we ask the backend for the
 *      authorize URL, then briefly hold the animation before redirecting
 *   2. return   — the backend redirects back with ?qbo=connected|error; we
 *      hold "Finalizing…" for a beat, refresh status, then flip to connected
 *
 * Returns { phase: "idle" | "connecting" | "connected", message, start }.
 * `connected` is the panel's real server-side status, so the resting state is
 * always truthful — the transient phases are only ever layered on top.
 */
export default function useQboConnectFlow({ authorizePath, connected, reload }) {
  const [transient, setTransient] = useState(null); // { message } while animating
  const [justConnected, setJustConnected] = useState(false);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("qbo");
    if (!result) return undefined;

    // Clean the URL first so a refresh doesn't replay the animation.
    params.delete("qbo");
    const qs = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);

    let cancelled = false;
    (async () => {
      if (result === "connected") {
        setTransient({ message: "Finalizing connection…" });
        await sleep(1400);
        await reloadRef.current();
        if (cancelled) return;
        setTransient(null);
        setJustConnected(true);
        toast.success("QuickBooks connected");
        setTimeout(() => !cancelled && setJustConnected(false), 4000);
      } else {
        toast.error("QuickBooks connection didn't complete. Please try again.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const start = async () => {
    setTransient({ message: "Requesting authorization…" });
    try {
      const { data } = await api.post(authorizePath);
      setTransient({ message: "Redirecting to Intuit…" });
      await sleep(900); // long enough to actually see the link light up
      window.location.href = data.url;
    } catch (e) {
      setTransient(null);
      toast.error(e.response?.data?.detail || "Failed to start QuickBooks connect");
    }
  };

  const phase = transient ? "connecting" : connected ? "connected" : "idle";
  return { phase, message: transient?.message, justConnected, start };
}
